import { useMemo, useState } from 'react'

import { DashCard } from './DashboardCards'
import { useHodDirectory } from '../../../hooks/useHodDirectory'
import { tallyTop, type TallyResult, type TallyRow } from '../../../lib/dashboard-insights'
import { prettyName } from '../../../lib/mandal-name'
import { SegmentedControl } from '../../ui/SegmentedControl'

import type { HodDirectoryEntry } from '../../../api/hodApi'
import type { GisIssue, GisSite } from '../../../api/types'
import type { BoardShortcuts } from '../../../hooks/useBoardShortcuts'

/** The cuts the review team reads the open load by (issue TYPE is its own donut, not repeated here). */
type DimView = 'department' | 'taluka' | 'hod' | 'site'

const VIEW_ITEMS: { key: DimView; label: string }[] = [
  { key: 'department', label: 'Department' },
  { key: 'taluka', label: 'Taluka' },
  { key: 'hod', label: 'HOD' },
  { key: 'site', label: 'Site' },
]

/** Plural noun for the "X of Y … have open issues" marksheet line. */
const NOUN: Record<DimView, string> = {
  department: 'departments',
  taluka: 'talukas',
  hod: 'HODs',
  site: 'sites',
}

const TOP = 6

/** HOD display name for a department (HOD ↔ department is 1:1), truthful fallback pre-directory. */
const hodLabel = (department: string, directory: HodDirectoryEntry[] | undefined): string =>
  directory?.find((d) => d.department === department)?.hodName ?? `HOD — ${department}`

/** Count distinct non-null keys — how many of a dimension actually carry an open issue. */
function distinctWith(list: GisIssue[], pick: (i: GisIssue) => string | null): number {
  const set = new Set<string>()
  for (const i of list) {
    const v = pick(i)
    if (v !== null) {
      set.add(v)
    }
  }
  return set.size
}

/** Tally bars: label · bar (red leading segment = critical share) · count · critical note. Each row
 *  is a multi-select toggle; the selected ones highlight. */
function DimBars({ rows, selected, onPick, pickTitle }: {
  rows: TallyRow[]
  selected: readonly string[]
  onPick: (r: TallyRow) => void
  pickTitle: string
}) {
  const max = Math.max(1, ...rows.map((r) => r.count))
  return (
    <div className="flex flex-col gap-[7px]">
      {rows.map((r) => {
        const on = r.value !== null && selected.includes(r.value)
        return (
        <button
          key={r.label}
          type="button"
          onClick={() => onPick(r)}
          title={pickTitle}
          aria-pressed={on}
          className={`-mx-1 grid cursor-pointer grid-cols-[minmax(0,148px)_minmax(0,1fr)_auto] items-center gap-3 rounded-[8px] px-1 py-[3px] text-left transition-colors focus-visible:ring-2 focus-visible:ring-blue-500/60 ${on ? 'bg-[#eef3fb]' : 'hover:bg-[#f5f7fa]'}`}
        >
          <span className={`min-w-0 truncate text-[12px] font-semibold ${on ? 'text-gis-navy' : 'text-gis-ink-2'}`} title={r.label}>
            {r.label}
          </span>
          <span className="flex h-[10px] min-w-0 overflow-hidden rounded-full bg-gis-track-2">
            <span
              className="block h-full bg-gis-red transition-[width] duration-[450ms] ease-[cubic-bezier(.2,.7,.3,1)]"
              style={{ width: `${(r.critical / max) * 100}%` }}
            />
            <span
              className="block h-full bg-gis-navy/55 transition-[width] duration-[450ms] ease-[cubic-bezier(.2,.7,.3,1)]"
              style={{ width: `${((r.count - r.critical) / max) * 100}%` }}
            />
          </span>
          <span className="flex items-center gap-2 text-[10.5px] tabular-nums">
            <span className="min-w-[22px] text-right font-bold text-gis-ink">{r.count}</span>
            <span className={r.critical > 0 ? 'font-semibold text-gis-red' : 'text-gis-soft'}>
              {r.critical > 0 ? `${r.critical} critical` : 'no critical'}
            </span>
          </span>
        </button>
        )
      })}
    </div>
  )
}

/** Which filter dimension a breakdown row toggles — dept/hod/site → the dashboard filter, mandal → page scope. */
type ToggleDim = 'department' | 'hod' | 'site' | 'mandal'

interface IssueDimensionCardProps {
  /** Open issues in the current scope. */
  openIssues: GisIssue[]
  /** Sites in scope — the issue→taluka join AND the "of N sites" denominator. */
  sites: GisSite[]
  /** Talukas (mandals) in scope — the "of N talukas" denominator. */
  mandalCount: number
  /** Cold-load flag (keepPreviousData keeps stale data during scope switches). */
  pending: boolean
  /** Selected values per dimension (row highlight). `mandal` = the page scope's active talukas. */
  selected: {
    department: readonly string[]
    hod: readonly string[]
    site: readonly string[]
    mandal: readonly string[]
  }
  /** Toggle a value into its filter (dept/hod/site → dashboard filter; mandal → page scope). */
  onToggle: (dim: ToggleDim, value: string) => void
  go: BoardShortcuts
}

/**
 * "Issue breakdown" for the Dashboard tab — the open load split BY the review team's cuts:
 * **Department | Taluka | HOD | Site** (tally bars, critical share as the red leading segment).
 * A marksheet line names how many of each dimension actually carry an issue vs the total ("3 of 12
 * departments have open issues · 47 open"). Rows are **multi-select filters**: dept/HOD/site toggle
 * into the dashboard filter (→ KPIs), taluka toggles the page's mandal scope. (Issue TYPE = its own donut.)
 */
export function IssueDimensionCard({ openIssues, sites, mandalCount, pending, selected, onToggle, go }: IssueDimensionCardProps) {
  const [view, setView] = useState<DimView>('department')
  const { data: directory } = useHodDirectory()
  const siteMandal = useMemo(() => new Map(sites.map((s) => [s.id, s.mandal])), [sites])

  const tally: TallyResult = useMemo(() => {
    if (view === 'taluka') {
      return tallyTop(
        openIssues,
        (i) => (i.siteId !== null ? (siteMandal.get(i.siteId) ?? null) : null),
        TOP,
        'Unknown taluka',
      )
    }
    if (view === 'site') {
      return tallyTop(openIssues, (i) => i.siteName, TOP, 'Unassigned site')
    }
    // department + hod both tally the owning department; only the label + drill differ.
    return tallyTop(openIssues, (i) => i.department, TOP, 'Unmapped')
  }, [openIssues, view, siteMandal])

  const rows: TallyRow[] = useMemo(() => {
    if (view === 'hod') {
      return tally.rows.map((r) => ({ ...r, label: r.value !== null ? hodLabel(r.value, directory) : r.label }))
    }
    if (view === 'taluka') {
      return tally.rows.map((r) => ({ ...r, label: r.value !== null ? prettyName(r.value) : r.label }))
    }
    return tally.rows
  }, [tally, view, directory])

  // "X of Y have open issues" — distinct dimension values carrying an open issue, over the universe.
  const summary = useMemo((): { withIssues: number; universe: number | null } => {
    if (view === 'taluka') {
      const withIssues = distinctWith(openIssues, (i) =>
        i.siteId !== null ? (siteMandal.get(i.siteId) ?? null) : null,
      )
      return { withIssues, universe: mandalCount }
    }
    if (view === 'site') {
      return { withIssues: distinctWith(openIssues, (i) => i.siteId), universe: sites.length }
    }
    // department + hod share the department universe (HOD ↔ department 1:1).
    return { withIssues: distinctWith(openIssues, (i) => i.department), universe: directory?.length ?? null }
  }, [openIssues, view, siteMandal, mandalCount, sites.length, directory])

  // taluka toggles the page mandal scope; the other three toggle the dashboard filter.
  const dimKey: ToggleDim = view === 'taluka' ? 'mandal' : view
  const selectedForView = selected[dimKey]
  const pick = (r: TallyRow): void => {
    if (r.value === null) {
      go.goIssues() // fallback buckets carry no real facet value — plain board, never a lying filter
    } else {
      onToggle(dimKey, r.value)
    }
  }
  const pickTitle =
    view === 'taluka'
      ? 'Toggle this taluka into the page scope'
      : 'Toggle this into the dashboard filter'

  return (
    <DashCard title="Issue breakdown" onOpen={() => go.goIssues()} openLabel="Open Issues board">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        {pending ? (
          <span className="text-[11px] text-gis-soft">Loading…</span>
        ) : (
          <span className="text-[11px] text-gis-soft">
            <b className="text-[13px] font-bold text-gis-ink tabular-nums">{summary.withIssues}</b>
            {summary.universe !== null && (
              <span className="font-semibold"> / {summary.universe}</span>
            )}{' '}
            {NOUN[view]} with issues · <b className="font-bold text-gis-ink-2 tabular-nums">{openIssues.length}</b> open
          </span>
        )}
        <SegmentedControl<DimView>
          aria-label="Break open issues down by"
          size="sm"
          track="b"
          shape="rounded"
          items={VIEW_ITEMS}
          value={view}
          onChange={setView}
        />
      </div>
      {pending ? (
        <div className="flex flex-col gap-2 py-1" aria-hidden>
          {[0, 1, 2, 3].map((n) => (
            <div key={n} className="h-[24px] animate-pulse rounded-[8px] bg-gis-track-2" />
          ))}
        </div>
      ) : openIssues.length === 0 ? (
        <div className="py-6 text-center text-[11.5px] text-gis-soft">No open issues in this selection.</div>
      ) : (
        <>
          <DimBars rows={rows} selected={selectedForView} onPick={pick} pickTitle={pickTitle} />
          {tally.more > 0 && (
            <p className="mt-2 text-[9.5px] text-gis-muted">
              +{tally.more} more — open the Issues board for the full split.
            </p>
          )}
        </>
      )}
    </DashCard>
  )
}
